/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import CloseIcon from '@material-ui/icons/Close';
import { formatNumber, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, isChartDataEmpty } from '../chartTheme';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartHeading } from '../ChartHeading';
import { ChartNotes } from '../ChartNotes';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartLimitNote } from '../ChartLimitNote';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick } from 'chart.js';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';
import { VideoModal } from '../../components/VideoModal';
import { AIIcon } from '../../components/icons/AIIcon';
import html2canvas from 'html2canvas';
import { AIChartDialog } from '../../components/Dialogs/AIChartDialog';
import { CurrencySymbolData, CURRENCY_SYMBOL_QUERY } from '../AgedUnbilledFees/AgedUnbilledFeesChartV2';
import { SearchCriteria } from '../SearchCriteria';
import { DrillDownType } from '../DrillDownType';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const TOP_FEE_EARNER_COUNT = 10;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '350px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
    }),
);

interface StaffUnbilledFeesChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

export const StaffUnbilledFeesChart: React.FC<StaffUnbilledFeesChartProps> = (props) => {
    const classes = useStyles();
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const [base64Data, setBase64Data] = useState<string | ArrayBuffer | null>(null);

    const [isAIDialogOpen, setIsAIDialogOpen] = useState<boolean>(false);

    const [isBlobGenerating, setIsBlobGenerating] = useState<boolean>(false);

    const [currencySymbol, setCurrencySymbol] = useState<string>('$');

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const chartName = 'Unbilled Fees By Fee Earner';

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    const staffUnbilledFeesQuery = useQuery<StaffUnbilledFeesData>(StaffUnbilledFeesQuery, {
        fetchPolicy: 'network-only',
        variables : {
            entityGuid: props.chartFilter && props.chartFilter.entityGuid !== undefined ? props.chartFilter.entityGuid : null,
            entityType: props.chartFilter && props.chartFilter.entityType !== undefined ? props.chartFilter.entityType : null,
            responsibility: props.chartFilter && props.chartFilter.responsibility !== undefined ? props.chartFilter.responsibility : null,
            include: null,
        }
    });

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setCurrencySymbol(currencySymbolQuery.data.options.currencySymbol.symbol);
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    const staffUnbilledFeesList = staffUnbilledFeesQuery.data?.statistics?.workInProgress?.staffUnbilledFeesList;

    const allStaffUnbilledFees = [...(staffUnbilledFeesList?.staffUnbilledFees ?? [])].sort((first, second) => second.unbilledFees - first.unbilledFees);

    const displayedStaffUnbilledFees = props.showAllRecords ? allStaffUnbilledFees : allStaffUnbilledFees.slice(0, TOP_FEE_EARNER_COUNT);

    const totalUnbilledFees = staffUnbilledFeesQuery.data?.statistics?.workInProgress?.staffUnbilledFeesList?.totalUnbilledFees;

    const unbilledFeesValues = displayedStaffUnbilledFees.map((item) => item.unbilledFees);

    const isTopRecordsOnly = !props.showAllRecords && (staffUnbilledFeesList?.recordCount ?? 0) > TOP_FEE_EARNER_COUNT;

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isStaffUnbilledFeesChartOpen`);
        }
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    // tslint:disable-next-line: no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const anchor = document.createElement('a');
        anchor.href = image;
        anchor.download = createFileName(extension, name);
        anchor.click();
    };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(chartName));
            // tslint:disable-next-line: align
        }, 500);
    };

    // tslint:disable-next-line: no-any
    const onAIGeneratedSuggestion = async (event: any) => {
        setIsBlobGenerating(true);

        event.preventDefault();
        event.stopPropagation();

        if (entireChartRef && entireChartRef.current) {
            const canvas = await html2canvas(entireChartRef.current);
            canvas.toBlob((blob) => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = async () => {
                        setBase64Data(reader.result);
                        setIsAIDialogOpen(true);
                    };
                }
            });
        }

        setIsBlobGenerating(false);
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo description={props.description} fontSize={props.showAllRecords ? 'large' : 'default'} />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="Learn more" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.isAIFeatureEnabled && (
                        <MTooltip title="AI Assistant">
                            <IconButton disabled={isBlobGenerating} size={props.showAllRecords ? 'medium' : 'small'} onClick={onAIGeneratedSuggestion}>
                                <AIIcon color="primary" fontSize={props.showAllRecords ? 'large' : 'default'} />
                            </IconButton>
                        </MTooltip>
                    )}
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'default'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {

            const searchCriterias = new Array<SearchCriteria>();

            props.getSelectedItem(
                searchCriterias,
                false,
                `Total ${chartName}`,
                DrillDownType.unbilledFeeEarnerDrilldown,
                'Unbilled Fees'
            );
        }        
    };

    if (staffUnbilledFeesQuery.loading && !staffUnbilledFeesQuery.data) {
        return (
            <div className={`${classes.loadingWrapper} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <>
            {isAIDialogOpen && (
                <AIChartDialog
                    fullWidth={true}
                    maxWidth="md"
                    open={isAIDialogOpen}
                    onClose={() => setIsAIDialogOpen(false)}
                    data={base64Data}
                    query={props.aiPrompt || 'Give me recommendations on this and highlight any exceptions or concerns'}
                    accessSubType={chartName}
                    layoutId={props.layoutId}
                />
            )}
            <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(unbilledFeesValues) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
                {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
                {chartControlComponent()}
                <div className={classes.titleContainer}>
                    <ChartHeading icon={<HourglassEmptyOutlinedIcon />} title={chartName} />
                    <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                        <AnimatedNumber value={totalUnbilledFees} format={(amount) => `${currencySymbol}${formatNumber(amount)}`} />
                    </div>
                </div>
                <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                    <Bar
                        ref={chartRef}
                        // tslint:disable-next-line: no-any
                        onClick={(event: React.MouseEvent<any>) => {
                            const clickedElements = getElementAtEvent(chartRef.current, event);
                            if (clickedElements.length > 0 && props.getSelectedItem) {
                                const clickedStaffUnbilledFee = displayedStaffUnbilledFees[clickedElements[0].index];

                                if (!clickedStaffUnbilledFee) {
                                    return;
                                }

                                const searchCriterias = new Array<SearchCriteria>();

                                searchCriterias.push({ property: 'StaffId', operator: 'eq', comparison: clickedStaffUnbilledFee.grouping });

                                props.getSelectedItem(
                                    searchCriterias,
                                    true,
                                    `${chartName} - ${clickedStaffUnbilledFee.format}`,
                                    DrillDownType.unbilledFeeEarnerDrilldown,
                                    'Unbilled Fees',
                                );
                            }
                        }}
                        data={{
                            labels: displayedStaffUnbilledFees.length > 0 ? displayedStaffUnbilledFees.map((item) => item.format) : [''],
                            datasets: [
                                {
                                    label: 'Unbilled Fees',
                                    ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                    data: displayedStaffUnbilledFees.length > 0 ? unbilledFeesValues : [0],
                                },
                            ],
                        }}
                        options={{
                            indexAxis: 'y' as const,
                            animation: rvChartAnimation(),
                            elements: { bar: { borderWidth: 0 } },
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                x: {
                                    grid: rvValueAxisStyle.grid,
                                    suggestedMax: getSuggestedMaxTick(unbilledFeesValues),
                                    beginAtZero: true,
                                    ticks: {
                                        ...rvValueAxisStyle.ticks,
                                        maxTicksLimit: getMaxTickLimit(unbilledFeesValues),
                                        autoSkip: false,
                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                            return `${currencySymbol}${formatNumber(tickValue)}`;
                                        },
                                    },
                                },
                                y: {
                                    grid: rvCategoryAxisStyle.grid,
                                    ticks: {
                                        ...rvCategoryAxisStyle.ticks,
                                        autoSkip: false,
                                    },
                                },
                            },
                            plugins: {
                                legend: {
                                    display: false,
                                },
                                title: {
                                    display: false,
                                    text: ``,
                                },
                                tooltip: {
                                    ...rvTooltipStyle,
                                    callbacks: {
                                        // tslint:disable-next-line: no-any
                                        label: function (context: any) {
                                            return `${currencySymbol}${formatNumber(unbilledFeesValues[context.dataIndex])}`;
                                        },
                                    },
                                },
                            },
                        }}
                    />
                    {isChartDataEmpty(unbilledFeesValues) && <ChartEmptyState title="No unbilled fees" hint="Nothing is unbilled right now." />}
                </div>
                {isTopRecordsOnly && <ChartLimitNote>The top {TOP_FEE_EARNER_COUNT} fee earners are shown</ChartLimitNote>}
                <ChartNotes notes={['Fees only, disbursement are not included']} />
            </div>
        </>
    );
};

export interface StaffUnbilledFeesData {
    statistics: Statistics;
}

export interface Statistics {
    workInProgress: WorkInProgress;
}

export interface WorkInProgress {
    staffUnbilledFeesList: StaffUnbilledFeesList;
}

export interface StaffUnbilledFeesList {
    recordCount: number;
    totalUnbilledFees: number;
    staffUnbilledFees: StaffUnbilledFee[];
}

export interface StaffUnbilledFee {
    grouping: string;
    format: string;
    unbilledFees: number;
}

const StaffUnbilledFeesQuery = gql`
    query UnbilledFeesByFeeEarner ($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            workInProgress {
                staffUnbilledFeesList (entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    recordCount
                    totalUnbilledFees
                    staffUnbilledFees {
                        grouping
                        format
                        unbilledFees
                    }
                }
            }
        }
    }
`;
